/**
 * Handing generated bytes to the user.
 *
 * Two paths, and the difference between them is what the caller is allowed to
 * believe afterwards:
 *
 * - `showSaveFilePicker()` reports the truth. The user picked a destination, or
 *   dismissed the picker, or the write failed.
 * - The Blob download reports only that the app did its part. The browser takes
 *   it from there and never says whether the file was kept, so `"saved"` from
 *   this path means "handed over", exactly as the port documents.
 *
 * There is no staging directory on either path. The native build wrote export
 * files to disk because an iOS share sheet needs a real file; a web build
 * generates the Markdown and offers it directly, which removes the
 * plaintext-accumulation risk staging existed to manage (spec §9.3) rather than
 * managing it again.
 */

import type { ExportOutcome, ExportPort } from "@ff/domain/ports";
import { assertSafeExportFilename } from "@ff/domain/ports";

export interface WritableFileLike {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

export interface SaveFileHandleLike {
  createWritable(): Promise<WritableFileLike>;
}

export interface SaveFilePickerOptions {
  readonly suggestedName: string;
  readonly types?: ReadonlyArray<{
    readonly description: string;
    readonly accept: Readonly<Record<string, readonly string[]>>;
  }>;
}

export interface AnchorLike {
  href: string;
  download: string;
  rel: string;
  click(): void;
  remove(): void;
}

export interface ExportDocumentLike {
  createElement(tagName: "a"): AnchorLike;
  readonly body: { appendChild(node: AnchorLike): unknown };
}

export interface ExportEnvironment {
  /** Absent everywhere except Chromium; the download path covers the rest. */
  readonly showSaveFilePicker:
    | ((options: SaveFilePickerOptions) => Promise<SaveFileHandleLike>)
    | undefined;
  readonly createObjectUrl: ((blob: Blob) => string) | undefined;
  readonly revokeObjectUrl: ((url: string) => void) | undefined;
  readonly document: ExportDocumentLike | undefined;
  /**
   * Defers the revoke. Revoking in the same task as the click cancels the
   * download in some browsers, because the transfer has not started yet.
   */
  readonly deferRevoke: (revoke: () => void) => void;
}

export function browserExportEnvironment(): ExportEnvironment {
  const picker = (globalThis as { showSaveFilePicker?: unknown }).showSaveFilePicker;
  const url = globalThis.URL as
    | { createObjectURL?: unknown; revokeObjectURL?: unknown }
    | undefined;

  return {
    showSaveFilePicker:
      typeof picker === "function"
        ? (picker as (options: SaveFilePickerOptions) => Promise<SaveFileHandleLike>).bind(
            globalThis,
          )
        : undefined,
    createObjectUrl:
      typeof url?.createObjectURL === "function"
        ? (url.createObjectURL as (blob: Blob) => string).bind(url)
        : undefined,
    revokeObjectUrl:
      typeof url?.revokeObjectURL === "function"
        ? (url.revokeObjectURL as (value: string) => void).bind(url)
        : undefined,
    document: globalThis.document as ExportDocumentLike | undefined,
    deferRevoke: (revoke) => {
      globalThis.setTimeout(revoke, 0);
    },
  };
}

/** The picker was not usable; it is not the user's answer. */
type PickerResult = ExportOutcome | "unavailable";

export class BrowserExport implements ExportPort {
  readonly #environment: ExportEnvironment;

  constructor(environment: ExportEnvironment = browserExportEnvironment()) {
    this.#environment = environment;
  }

  async offer(filename: string, mimeType: string, bytes: Uint8Array): Promise<ExportOutcome> {
    // Inside the async body on purpose: an unsafe name rejects rather than
    // throwing synchronously, so a caller has one failure channel instead of a
    // `try` wrapped around an `await`.
    assertSafeExportFilename(filename);

    const picker = this.#environment.showSaveFilePicker;
    if (picker !== undefined) {
      const outcome = await this.#offerThroughPicker(picker, filename, mimeType, bytes);
      if (outcome !== "unavailable") return outcome;
    }
    return this.#offerAsDownload(filename, mimeType, bytes);
  }

  async #offerThroughPicker(
    picker: (options: SaveFilePickerOptions) => Promise<SaveFileHandleLike>,
    filename: string,
    mimeType: string,
    bytes: Uint8Array,
  ): Promise<PickerResult> {
    let handle: SaveFileHandleLike;
    try {
      handle = await picker(saveFilePickerOptions(filename, mimeType));
    } catch (error) {
      // `AbortError` is the user closing the picker — an ordinary answer, and
      // the one case where falling through to a download would be wrong: they
      // said no, and a file would appear anyway.
      if (errorName(error) === "AbortError") return "cancelled";
      // Anything else is the picker refusing to appear — no user activation, a
      // cross-origin frame, an accept type it rejected. The user still asked for
      // their file, so the download path gets a turn.
      return "unavailable";
    }

    try {
      const writable = await handle.createWritable();
      await writable.write(bytes);
      // The bytes are not on disk until the stream closes, so a close that
      // throws is a failed export even though the write resolved.
      await writable.close();
    } catch {
      // The user already chose a destination. Falling back to a download here
      // would drop a second copy somewhere they did not pick, so this reports
      // the failure and lets the caller — which still holds the content — decide.
      return "failed";
    }
    return "saved";
  }

  #offerAsDownload(filename: string, mimeType: string, bytes: Uint8Array): ExportOutcome {
    const { createObjectUrl, revokeObjectUrl, document, deferRevoke } = this.#environment;
    // No object URLs and no document means no way to hand anything over. The
    // caller still holds the content and can show it instead.
    if (createObjectUrl === undefined || document === undefined) return "failed";

    let url: string | undefined;
    try {
      // Copied into a plain `ArrayBuffer` rather than wrapped: the caller's view
      // may sit on a shared or pooled buffer, and a Blob that aliased it would
      // change under the download if the caller reused the buffer.
      const buffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buffer).set(bytes);
      url = createObjectUrl(new Blob([buffer], { type: mimeType }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = "noopener";
      // Appended before clicking: a detached anchor's click is ignored in
      // Firefox, which turns this into a silent no-op rather than a download.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      if (url !== undefined) revokeObjectUrl?.(url);
      return "failed";
    }

    const objectUrl = url;
    deferRevoke(() => {
      revokeObjectUrl?.(objectUrl);
    });
    return "saved";
  }
}

function saveFilePickerOptions(filename: string, mimeType: string): SaveFilePickerOptions {
  const dot = filename.lastIndexOf(".");
  const extension = dot > 0 ? filename.slice(dot) : undefined;
  // `types` is omitted rather than sent empty for a name with no extension: the
  // picker rejects an accept entry with no extensions, and a rejected picker
  // would drop an ordinary export onto the fallback path for no reason.
  if (extension === undefined) return { suggestedName: filename };
  return {
    suggestedName: filename,
    types: [{ description: mimeType, accept: { [mimeType]: [extension] } }],
  };
}

function errorName(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const name = (error as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
}
